import { useEffect, useRef, useState } from 'react';
import {
  StepDriver,
  buildableAt,
  canBuild,
  canMerge,
  createWorld,
  restoreWorld,
  snapshotWorld,
  type WorldSnapshot,
  ringMass,
  type Content,
  type World,
} from '@playnets/sim';
import { loadContent } from '@playnets/data';
import { GameRenderer } from '@playnets/render-pixi';
import { snapshotHud, useUi } from '../store';
import { InputController } from './input';

export interface GameHandle {
  world: World;
  content: Content;
  input: InputController;
  renderer: GameRenderer;
  /** Rewind to the start of the current wave's build phase. */
  restartWave: () => void;
  /** Nearest mount the carried card would act on — the bar's drop test. */
  dropTargetAt: (clientX: number, clientY: number, structureId: string) => number | null;
  /** Place the carried card on a mount, if it will actually go there. */
  place: (slotId: number, structureId: string) => boolean;
}

export interface GameOptions {
  planetId: string;
  levelId: string;
  evolutions: readonly string[];
  unlockedStructures: readonly string[];
  seed: number;
}

/** HUD is pushed at 10Hz, not 60Hz (§12.2). */
const HUD_INTERVAL = 0.1;
/** The build sheet covers the southern corridor, so the game slows (§9). */
const SHEET_SPEED = 0.35;

/**
 * Reported from inside the loop, exactly once, at the moment the level
 * resolves. It used to be an effect in <Battle> watching the HUD — but the
 * same state change unmounts <Battle>, so the effect raced its own teardown
 * and a win could be recorded as a loss.
 */
export type EndHandler = (won: boolean, damage: number, coreMax: number) => void;

export function useGame(
  holderRef: React.RefObject<HTMLElement | null>,
  overlayRef: React.RefObject<HTMLElement | null>,
  opts: GameOptions,
  onEnd: EndHandler,
): GameHandle | null {
  const [handle, setHandle] = useState<GameHandle | null>(null);
  const handleRef = useRef<GameHandle | null>(null);

  useEffect(() => {
    const holder = holderRef.current;
    const overlay = overlayRef.current;
    if (!holder || !overlay) return;

    let disposed = false;
    let raf = 0;
    let detachInput: (() => void) | null = null;
    let renderer: GameRenderer | null = null;
    let observer: ResizeObserver | null = null;

    const content = loadContent();
    const world = createWorld({
      content,
      planetId: opts.planetId,
      levelId: opts.levelId,
      seed: opts.seed,
      evolutions: opts.evolutions,
      unlockedStructures: opts.unlockedStructures,
    });

    const ui = useUi.getState();
    let ended = false;
    let voiceIndex = 0;
    const say = (lines: readonly string[]): void => {
      if (lines.length === 0) return;
      useUi.getState().setSay(lines[voiceIndex++ % lines.length]!);
    };

    void (async () => {
      const r = await GameRenderer.create(world, content, {
        evolutions: opts.evolutions,
        seed: opts.seed,
      });
      if (disposed) {
        r.destroy();
        return;
      }
      renderer = r;
      holder.appendChild(r.canvas);

      const fit = (): void => {
        const rect = overlay.getBoundingClientRect();
        r.resize(rect.width, rect.height);
      };
      fit();
      observer = new ResizeObserver(fit);
      observer.observe(overlay);

      /**
       * Would the carried card do anything here? Structural only — price is
       * left out on purpose, so a mount you can't currently afford still
       * lights up and tells you what you're saving toward, exactly like the
       * dimmed card it came from.
       */
      const accepts = (slotId: number, structureId: string): boolean => {
        const slot = world.slots[slotId];
        if (!slot) return false;
        if (slot.structure) {
          const why = canMerge(world, content, slot, structureId);
          return why.ok || why.reason === 'juice';
        }
        const why = canBuild(world, content, slot, structureId).reason;
        return why !== 'wrong-layer' && why !== 'locked' && why !== 'not-here'
          && why !== 'exclusive' && why !== 'merge-only' && why !== 'unknown';
      };

      /**
       * Commits a placement — a build on an empty mount, a merge on a maxed one
       * of the same type. Returns whether it actually went down, so the caller
       * knows whether to put the card back: a drop you can't afford should
       * leave you still holding it rather than silently swallowing the gesture.
       */
      const place = (slotId: number, structureId: string): boolean => {
        const slot = world.slots[slotId];
        if (!slot) return false;
        if (slot.structure) {
          if (!canMerge(world, content, slot, structureId).ok) return false;
          input.push({ kind: 'merge', slot: slotId, structure: structureId });
          return true;
        }
        if (!canBuild(world, content, slot, structureId).ok) return false;
        input.push({ kind: 'build', slot: slotId, structure: structureId });
        return true;
      };

      const input = new InputController(
        () => world,
        (x, y) => r.toLogical(x, y),
        {
          onSlotTap: (id) => {
            const ui = useUi.getState();
            // Carrying a card? A mount tap is a placement, not a menu. This is
            // the tap half of drag-to-place: take a card, tap where it goes.
            if (ui.armed !== null) {
              if (place(id, ui.armed)) ui.arm(null);
              return;
            }
            // Empty mounts have no menu any more — the bar is the menu. Only a
            // mount with something on it has anything to say (upgrade, sell).
            if (!world.slots[id]?.structure) return;
            ui.openSheet({ kind: 'slot', id });
          },
          onPlanetTap: () => {
            useUi.getState().arm(null);
            useUi.getState().openSheet({ kind: 'planet' });
          },
          onEmptyTap: () => {
            // Tapping the board puts the carried card back on the bar.
            useUi.getState().arm(null);
            useUi.getState().openSheet(null);
          },
          onDragStart: () => {
            if (useUi.getState().sheet !== null) useUi.getState().openSheet(null);
          },
        },
        (slotId) => {
          const slot = world.slots[slotId];
          return slot ? buildableAt(world, content, slot).length > 0 : false;
        },
        () => useUi.getState().armed,
        accepts,
      );
      detachInput = input.attach(overlay);

      const driver = new StepDriver(world, content);
      let last = performance.now();
      let hudTimer = 0;

      // A restore point per build phase, so "restart wave" puts back the
      // structures, juice and Integrity the player had before they committed.
      let checkpoint: WorldSnapshot = snapshotWorld(world);
      let checkpointWave = world.waveIndex;

      const frame = (now: number): void => {
        if (disposed) return;
        raf = requestAnimationFrame(frame);

        const elapsed = Math.min((now - last) / 1000, 0.25);
        last = now;

        const state = useUi.getState();
        const speed = state.paused
          ? 0
          : state.sheet !== null
            ? SHEET_SPEED
            : state.speed;

        r.setSelectedSlot(state.sheet?.kind === 'slot' ? state.sheet.id : null);
        r.setPlacing(state.armed, state.hoverSlot);
        r.snapshot();
        const alpha = driver.advance(elapsed, input.consume(), speed);

        if (world.waveState === 'prep' && world.waveIndex !== checkpointWave) {
          checkpointWave = world.waveIndex;
          checkpoint = snapshotWorld(world);
        }

        drainEvents(world, r, say);

        hudTimer += elapsed;
        if (hudTimer >= HUD_INTERVAL) {
          hudTimer = 0;
          useUi.getState().pushHud(snapshotHud(world, ringMass(world, content)));
          if (!ended && (world.waveState === 'won' || world.waveState === 'lost')) {
            ended = true;
            // Report before switching screens: the switch tears this component
            // down, so anything left to a later effect never runs.
            onEnd(world.waveState === 'won', world.coreDamageTaken, world.coreMax);
            useUi.getState().setScreen('results');
          }
        }

        r.draw(alpha, elapsed);
      };
      raf = requestAnimationFrame(frame);

      const h: GameHandle = {
        world,
        content,
        input,
        renderer: r,
        dropTargetAt: (x, y, structureId) => input.dropTargetAt(x, y, structureId),
        place,
        restartWave: () => {
          restoreWorld(world, checkpoint);
          ended = false;
          useUi.getState().openSheet(null);
          useUi.getState().setPaused(false);
          useUi.getState().pushHud(snapshotHud(world, ringMass(world, content)));
        },
      };
      handleRef.current = h;
      setHandle(h);

      // Push one snapshot immediately so the HUD isn't blank on the first frame.
      useUi.getState().pushHud(snapshotHud(world, ringMass(world, content)));
      say(world.planet.voice.onWave);
    })();

    void ui;

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      detachInput?.();
      observer?.disconnect();
      renderer?.destroy();
      handleRef.current = null;
      setHandle(null);
    };
    // A change to any of these is a different run, so a full rebuild is correct.
  }, [
    holderRef,
    overlayRef,
    opts.planetId,
    opts.levelId,
    opts.seed,
    opts.evolutions,
    opts.unlockedStructures,
    onEnd,
  ]);

  return handle;
}

/**
 * Sim events are drained here and turned into presentation: a placard line in
 * the poster margin, and a flinch through the planet body rather than a
 * screen flash. The sim never learns any of this happened.
 */
function drainEvents(
  world: World,
  renderer: GameRenderer,
  say: (lines: readonly string[]) => void,
): void {
  if (world.events.length === 0) return;
  const voice = world.planet.voice;
  for (const ev of world.events) {
    switch (ev.type) {
      case 'core-hit':
        renderer.flinch(Math.min(1, (ev.amount ?? 5) / 14));
        say(voice.onHurt);
        break;
      case 'ultimate':
        say(voice.onUltimate);
        break;
      case 'wave-start':
        say(voice.onWave);
        break;
      case 'wave-clear':
        say(voice.onClear);
        break;
      default:
        break;
    }
  }
  world.events.length = 0;
}
